import { useCallback, useEffect, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"

import { ShieldX } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { useRoutes } from "@/contexts/use-routes"
import { ProjectChatHistory } from "@/features/projects/components/project-chat-history"
import { ProjectChatInput } from "@/features/projects/components/project-chat-input"
import { ProjectEmptyState } from "@/features/projects/components/project-empty-state"
import { ProjectPageHeader } from "@/features/projects/components/project-page-header"
import { ProjectSettingsDialog } from "@/features/projects/components/project-settings-dialog"
import { ProjectShareDialog } from "@/features/projects/components/project-share-dialog"
import { ProjectsDialog } from "@/features/projects/components/projects-dialog"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useProject } from "@/hooks/use-projects"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ProjectService } from "@/services/project-service"
import { useAISettingsStore } from "@/stores/ai-settings-store"
import { getProjectsListPath } from "@/utils/route-helpers"

export function ProjectsPage() {
  const { projectId } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { routes } = useRoutes()

  // Determine sessionType from current route path (e.g., /custom-variant/projects/:id)
  const sessionType = useCurrentSessionType()

  const handleLeaveProject = useCallback(() => {
    const destination = getProjectsListPath(sessionType, routes)
    void navigate(destination)
  }, [navigate, sessionType, routes])

  const queryClient = useQueryClient()
  const { data: project, isLoading, isFetched } = useProject(projectId, sessionType)

  // Load available model list from settings store using dynamic sessionType
  const initSettings = useAISettingsStore((state) => state.initSettings)

  useEffect(() => {
    if (projectId) {
      initSettings(sessionType)
    }
  }, [initSettings, sessionType, projectId])

  // Mark project as viewed when user navigates to it
  useEffect(() => {
    if (projectId && project?.hasUnviewedNotification) {
      ProjectService.markProjectViewed(projectId)
        .then(() => {
          // Invalidate projects query to refresh the indicator
          void queryClient.invalidateQueries({ queryKey: queryKeys.projects(sessionType) })
        })
        .catch((error) => {
          logger.error("Failed to mark project as viewed:", error)
        })
    }
  }, [projectId, project?.hasUnviewedNotification, queryClient, sessionType])

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Handle no projectId case: the bare projects route is an empty state with a
  // Create Project CTA (DESIGN.md: every empty state needs a primary action).
  if (!projectId) {
    return (
      <>
        <ProjectEmptyState onCreateProject={() => setCreateDialogOpen(true)} />
        <ProjectsDialog
          open={createDialogOpen}
          sessionType={sessionType}
          onOpenChange={setCreateDialogOpen}
        />
      </>
    )
  }

  // Check if project was not found after fetching
  const projectNotFound = isFetched && !project

  // Handle the "project not found" case with a proper UI
  if (projectNotFound) {
    return (
      <div className="mx-auto mt-24 max-w-md px-6 text-center">
        <div className="bg-destructive/10 mb-6 inline-flex rounded-full p-8">
          <ShieldX className="text-destructive/60 size-12" />
        </div>
        <h2 className="mb-2 text-xl font-semibold">
          {t("projects.not_found_title", { defaultValue: "Project Not Found" })}
        </h2>
        <p className="text-muted-foreground mb-6">
          {t("projects.not_found_description", {
            defaultValue: "This project doesn't exist or you don't have permission to access it.",
          })}
        </p>
        <Button variant="default" onClick={() => navigate("/")}>
          {t("common.back", { defaultValue: "Back" })}
        </Button>
      </div>
    )
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex w-full justify-center pt-24">
        <Spinner className="size-8" />
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col">
      <ProjectPageHeader
        project={project}
        onSettingsClick={() => setSettingsDialogOpen(true)}
        onShareClick={() => setShareDialogOpen(true)}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="mx-auto flex h-full max-w-4xl flex-col px-3 py-4 sm:px-6 sm:py-8">
          {/* Chat Input Section */}
          <div className="mb-4 sm:mb-8">
            <ProjectChatInput
              projectId={projectId}
              sessionType={sessionType}
              onSent={() => {
                void queryClient.invalidateQueries({
                  queryKey: queryKeys.sessionHistory(sessionType, projectId),
                })
              }}
            />
          </div>

          {/* Chat History Section */}
          <div className="flex-1 overflow-hidden">
            <div className="bg-muted/30 h-full rounded-xl">
              <div className="p-3 sm:p-6">
                <ScrollArea className="h-full">
                  <ProjectChatHistory projectId={projectId} sessionType={sessionType} />
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProjectSettingsDialog
        open={settingsDialogOpen}
        project={project}
        projectId={projectId}
        sessionType={sessionType}
        onOpenChange={setSettingsDialogOpen}
      />

      <ProjectShareDialog
        open={shareDialogOpen}
        project={project}
        projectId={projectId}
        onLeave={handleLeaveProject}
        onOpenChange={setShareDialogOpen}
      />
    </div>
  )
}
